import type {MouseEventHandler} from "react";
import {useState} from "react";

const someNodeHasContraint = (
  node: HTMLElement | null,
  limitNode: HTMLElement,
  checkFn: (node: HTMLElement) => boolean
) => {
  if (!node || node === limitNode) return false;
  if (checkFn(node)) return true;
  return someNodeHasContraint(node.parentElement, limitNode, checkFn);
};

const isInteractive = (node: HTMLElement) => {
  return node.tagName === "BUTTON" || node.tagName === "A" || node.tagName === "INPUT";
};

const useLongPress = ({onClick, onLongClick}: {onClick?: () => void; onLongClick?: () => void}) => {
  const [downInfo, setDownInfo] = useState<null | {
    ts: number;
    x: number;
    y: number;
    timeoutId: ReturnType<typeof setTimeout>;
    handled: boolean;
  }>(null);
  const handleUp: MouseEventHandler<HTMLElement> = (e) => {
    if (!downInfo) return;
    const {x, y, timeoutId, handled} = downInfo;
    if (handled) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    clearTimeout(timeoutId);
    const diff = (e.clientX - x) ** 2 + (e.clientY - y) ** 2;
    if (diff > 30) return;
    if (onClick) onClick();
  };
  const handleDown: MouseEventHandler<HTMLElement> = (e) => {
    if (someNodeHasContraint(e.target as HTMLElement, e.currentTarget, isInteractive)) return;
    setDownInfo({
      ts: new Date().getTime(),
      x: e.clientX,
      y: e.clientY,
      handled: false,
      timeoutId: setTimeout(() => {
        if (onLongClick) {
          // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
          if (navigator.vibrate) navigator.vibrate(25);
          setDownInfo((prev) => ({...prev!, handled: true}));
          onLongClick();
        }
        setTimeout(() => setDownInfo(null), 100);
      }, 300),
    });
  };

  const handleMove: MouseEventHandler<HTMLElement> = (e) => {
    if (!downInfo) return;
    const {x, y, timeoutId} = downInfo;
    const diff = (e.clientX - x) ** 2 + (e.clientY - y) ** 2;
    if (diff > 30) clearTimeout(timeoutId);
  };

  return {
    onPointerDown: handleDown,
    onPointerUp: handleUp,
    onPointerMove: handleMove,
  };
};
export default useLongPress;
